/**
 * MAX-POWER ENVELOPE STUDY — the frozen projection.
 *
 * Assembles the sweep into a versioned snapshot, identifies which constraint
 * actually BINDS, and inverts the winning capacity back through the power stack
 * so the headline answers the question a user actually asks: "how big a
 * datacenter can this site host?".
 *
 * Shape and versioning discipline are copied from `GenerationSizingStudySnapshot`
 * (its mirror image — that study minimises, this one maximises):
 *   - its own schemaVersion / modelVersion, independent of the exhibit's
 *   - an `unavailable` shell that still carries a full `basis` + `warnings`, so
 *     a report renders a stated reason rather than a hole
 *   - `status` / `reason` as the only availability signal
 *
 * It lives OUTSIDE `GenerationImpactResult` deliberately: the result is
 * byte-locked by the publish-gate replay, and this study depends on parcels,
 * layout and polygon clipping — the highest-drift-risk computation in the repo.
 */

import {
  GENERATION_MAX_POWER_STUDY_MODEL_VERSION,
  GENERATION_MAX_POWER_STUDY_SCHEMA_VERSION,
} from '../../types/generationImpact';
import type {
  GenerationMaxPowerConstraintId,
  GenerationMaxPowerLadderRung,
  GenerationMaxPowerStudySnapshot,
} from '../../types/generationImpact';
import type { PueSource } from '../powerStack';
import { resolveMaxPowerStack } from '../powerStack';
import { solveGenerationImpact } from './solveGenerationImpact';
import { hashGenerationImpactPayload } from './integrity';
import {
  sweepMaxGenerationCapacity,
  type SweepMaxGenerationCapacityInput,
  type SweepMaxGenerationCapacityOptions,
} from './sweepMaxGenerationCapacity';

const DEFAULT_RESERVE_MARGIN_PCT = 15;

export interface BuildMaxPowerStudyContext {
  /** Intent-resolved PUE / PF for the inverse stack. Defaults apply when absent. */
  pue?: number;
  pueSource?: PueSource;
  powerFactor?: number;
}

function emptyBasis(
  args: SweepMaxGenerationCapacityInput,
): GenerationMaxPowerStudySnapshot['basis'] {
  return {
    baselineInputHash: hashGenerationImpactPayload(args.input),
    baselineResultHash: '',
    axis: 'uniform-block-rating',
    axisLabel: 'ISO gross MW per block',
    fixedBlockCount: null,
    ladderIncrementMW: null,
    ladderMinRung: 0,
    ladderMaxRung: 0,
    evaluatedRungCount: 0,
    incrementCoarsenedFromMW: null,
    contingencyOutages: args.input.contingencyOutages ?? 1,
    reserveMarginPct: args.input.reserveMarginPct ?? DEFAULT_RESERVE_MARGIN_PCT,
    reserveMarginDefaulted: args.input.reserveMarginPct == null,
    landBasis: 'unavailable',
    layoutRotationDeg: args.layout?.rotationDeg ?? null,
    parcelId: args.layout?.targetParcelId ?? null,
  };
}

/**
 * Which constraints flipped from pass at the maximum to fail at the very next
 * rung. That difference — not a min() over independent ceilings — is what
 * actually binds, because the land test is non-monotone and a min() can select a
 * capacity that fails it.
 */
function identifyBinders(
  rungs: readonly GenerationMaxPowerLadderRung[],
  maxRung: GenerationMaxPowerLadderRung,
): GenerationMaxPowerConstraintId[] {
  const next = rungs.find(r => r.index > maxRung.index);
  if (!next) return [];
  const verdictAt = (rung: GenerationMaxPowerLadderRung, id: GenerationMaxPowerConstraintId) =>
    rung.constraintVerdicts.find(v => v.id === id)?.verdict;
  return maxRung.constraintVerdicts
    .filter(v => v.verdict === 'pass' && verdictAt(next, v.id) === 'fail')
    .map(v => v.id);
}

/**
 * Land is ENUMERATED-only, so it never appears in the analytic `ceilings` array.
 * Without a static fallback a binding statement would print a raw slug at the
 * user ("land-parcel-fit" rather than "Parcel fit (geometry)").
 */
const STATIC_CONSTRAINT_LABEL: Record<GenerationMaxPowerConstraintId, string> = {
  'land-parcel-fit': 'Parcel fit (geometry)',
  'gas-firm-capacity': 'Firm gas delivery capacity',
  'gas-interconnect-derate': 'Gas interconnect (pressure-derated)',
  'water-consumption': 'Water consumption capacity',
  'co2-export': 'CO₂ export capacity',
  'receptor-noise': 'Receptor noise threshold',
};

function constraintLabel(
  ceilings: readonly { id: GenerationMaxPowerConstraintId; label: string }[],
  id: GenerationMaxPowerConstraintId,
): string {
  return ceilings.find(c => c.id === id)?.label ?? STATIC_CONSTRAINT_LABEL[id] ?? id;
}

function round1(n: number): string {
  return String(Math.round(n * 10) / 10);
}

export function buildGenerationMaxPowerStudySnapshot(
  args: SweepMaxGenerationCapacityInput,
  context: BuildMaxPowerStudyContext = {},
  options: SweepMaxGenerationCapacityOptions = {},
): GenerationMaxPowerStudySnapshot {
  const sweep = sweepMaxGenerationCapacity(args, options);

  if (sweep.status === 'unavailable') {
    return {
      schemaVersion: GENERATION_MAX_POWER_STUDY_SCHEMA_VERSION,
      modelVersion: GENERATION_MAX_POWER_STUDY_MODEL_VERSION,
      status: 'unavailable',
      reason: sweep.reason,
      boundQuality: null,
      basis: emptyBasis(args),
      ceilings: [],
      bindingConstraintIds: [],
      bindingStatement: sweep.reason ?? 'The max-power study is unavailable for this scenario.',
      maximum: null,
      ladder: [],
      landFitNonMonotonic: false,
      warnings: sweep.warnings,
    };
  }

  const baseResult = solveGenerationImpact(args.input);
  const basis: GenerationMaxPowerStudySnapshot['basis'] = {
    baselineInputHash: hashGenerationImpactPayload(args.input),
    baselineResultHash: hashGenerationImpactPayload(baseResult),
    axis: sweep.axis,
    axisLabel: sweep.axisLabel,
    fixedBlockCount: sweep.fixedBlockCount,
    ladderIncrementMW: sweep.ladderIncrementMW,
    ladderMinRung: sweep.rungs[0]?.index ?? 0,
    ladderMaxRung: sweep.rungs[sweep.rungs.length - 1]?.index ?? 0,
    evaluatedRungCount: sweep.rungs.length,
    incrementCoarsenedFromMW: sweep.incrementCoarsenedFromMW,
    contingencyOutages: args.input.contingencyOutages ?? 1,
    reserveMarginPct: args.input.reserveMarginPct ?? DEFAULT_RESERVE_MARGIN_PCT,
    reserveMarginDefaulted: args.input.reserveMarginPct == null,
    landBasis: sweep.landBasis,
    layoutRotationDeg: args.layout?.rotationDeg ?? null,
    parcelId: args.layout?.targetParcelId ?? null,
  };

  // THE MAXIMUM: the highest rung where every documented constraint passes
  // SIMULTANEOUSLY — verified by a real solve, not inferred from a min().
  const feasible = sweep.rungs.filter(r => r.feasible);
  const maxRung = feasible.length > 0 ? feasible[feasible.length - 1] : null;

  if (!maxRung) {
    // NAME what failed. "No capacity satisfied every constraint" is true but
    // useless — the user needs to know whether the parcel is too small, the gas
    // lateral too thin, or the water source too limited before they can act.
    const smallest = sweep.rungs[0];
    const failedAtSmallest = smallest
      ? smallest.constraintVerdicts.filter(v => v.verdict === 'fail').map(v => v.id)
      : [];
    const failedLabels = failedAtSmallest.map(id => constraintLabel(sweep.ceilings, id));
    // Name the UNMEASURABLE screens separately from the violated ones. A user
    // told "the parcel is too small" will go buy land; a user told "the
    // clearance could not be computed" will go fix the layout.
    const unmeasured = smallest?.landFit?.screeningUnavailable
      ? ` Land fit could not be screened (${smallest.landFit.screeningUnavailableReason ?? 'screening unavailable'}),`
        + ' so it is reported as indeterminate rather than a land ceiling.'
      : '';
    // When LAND is the failing screen, quote the geometry. "The parcel is too
    // small" is not actionable on its own — a 1,000-acre parcel that fails a
    // 45-acre plant is a SHAPE/placement problem, not an area problem, and the
    // numbers are what tell the two apart.
    const lf = smallest?.landFit;
    const landDetail = lf && failedAtSmallest.includes('land-parcel-fit')
      ? ` Land: ${round1(lf.totalFootprintAcres)} ac of plant against`
        + `${lf.usableAcres != null ? ` a ${round1(lf.usableAcres)} ac parcel;` : ''}`
        + ` ${lf.blocksContained}/${lf.blocksPlaced} blocks contained`
        + `${lf.outsideAcres != null ? `, ${round1(lf.outsideAcres)} ac outside the boundary` : ''}`
        + `${lf.containmentPct != null ? ` (${round1(lf.containmentPct)}% contained)` : ''}.`
        + ' A large parcel that still fails is a SHAPE or placement problem, not an area shortfall —'
        + ' try a different rotation, setback, or target parcel in the Test-Fit Studio.'
      : '';
    const detail = failedLabels.length > 0
      ? ` Even the smallest evaluated capacity (${smallest.blockCount} × ${round1(smallest.isoGrossMWPerBlock)} MW)`
        + ` violates: ${failedLabels.join(', ')}.${landDetail}${unmeasured}`
      : unmeasured;
    return {
      schemaVersion: GENERATION_MAX_POWER_STUDY_SCHEMA_VERSION,
      modelVersion: GENERATION_MAX_POWER_STUDY_MODEL_VERSION,
      status: 'unavailable',
      reason: `No evaluated capacity satisfied every documented constraint.${detail}`,
      boundQuality: null,
      basis,
      ceilings: sweep.ceilings,
      bindingConstraintIds: failedAtSmallest,
      bindingStatement: detail.trim() || 'Even the smallest evaluated capacity violates a documented constraint.',
      maximum: null,
      ladder: sweep.rungs,
      landFitNonMonotonic: sweep.landFitNonMonotonic,
      warnings: sweep.warnings,
    };
  }

  const binders = identifyBinders(sweep.rungs, maxRung);
  const anyIndeterminate = maxRung.constraintVerdicts.some(v => v.verdict === 'indeterminate');

  // boundQuality is NEVER 'exact'. The documented constraint set is incomplete
  // (see the permanent warning the sweep attaches), so the best achievable
  // label is "bounded by the constraints we actually have".
  const boundQuality: GenerationMaxPowerStudySnapshot['boundQuality'] =
    sweep.reachedSearchCeiling ? 'search-ceiling-reached'
      : anyIndeterminate ? 'upper-bound-undocumented-constraints'
        : 'bounded-by-documented-constraints';

  const inverse = resolveMaxPowerStack({
    firmNetGenerationMW: maxRung.firmNetMW,
    pue: context.pue,
    pueSource: context.pueSource,
    powerFactor: context.powerFactor,
  });

  const label = (id: GenerationMaxPowerConstraintId) => constraintLabel(sweep.ceilings, id);

  const bindingStatement = (() => {
    const size = `${maxRung.blockCount} × ${round1(maxRung.isoGrossMWPerBlock)} MW`
      + ` (${round1(maxRung.siteGrossMW)} MW site gross, ${round1(maxRung.firmNetMW)} MW firm)`;
    if (boundQuality === 'search-ceiling-reached') {
      return `No documented constraint bound this scenario within the evaluated range. `
        + `The search ceiling stopped the sweep at ${size} — read this as "AT LEAST", never as an equality.`;
    }
    if (binders.length === 0) {
      return `The maximum evaluated capacity is ${size}; no single constraint could be isolated as the binder.`;
    }
    const names = binders.map(label).join(' and ');
    return `${binders.length > 1 ? 'Co-binding: ' : ''}${names} bind${binders.length > 1 ? '' : 's'} at ${size}. `
      + `The next rung on the ladder violates ${binders.length > 1 ? 'them' : 'it'}.`;
  })();

  const warnings = [...sweep.warnings];
  if (anyIndeterminate) {
    const undocumented = maxRung.constraintVerdicts
      .filter(v => v.verdict === 'indeterminate')
      .map(v => label(v.id));
    warnings.unshift(
      `This figure is an UPPER BOUND, not a maximum: ${undocumented.join(', ')} `
      + `${undocumented.length > 1 ? 'are' : 'is'} not documented, so the true maximum can only be LOWER.`,
    );
  }

  return {
    schemaVersion: GENERATION_MAX_POWER_STUDY_SCHEMA_VERSION,
    modelVersion: GENERATION_MAX_POWER_STUDY_MODEL_VERSION,
    status: 'available',
    reason: null,
    boundQuality,
    basis,
    ceilings: sweep.ceilings,
    bindingConstraintIds: binders,
    bindingStatement,
    maximum: inverse
      ? {
        rungIndex: maxRung.index,
        blockCount: maxRung.blockCount,
        isoGrossMWPerBlock: maxRung.isoGrossMWPerBlock,
        isoGrossNameplateMW: maxRung.isoGrossNameplateMW,
        siteGrossMW: maxRung.siteGrossMW,
        normalNetMW: maxRung.normalNetMW,
        firmNetMW: maxRung.firmNetMW,
        servableCriticalLoadMW: maxRung.servableCriticalLoadMW,
        facilityMW: inverse.facilityMW,
        facilityMWAtGenerationTerminals: inverse.facilityMWAtGenerationTerminals,
        itLoadMW: inverse.itLoadMW,
        gridMW: inverse.gridMW,
        apparentMVA: inverse.apparentMVA,
        pue: inverse.pue,
        pueSource: inverse.pueSource,
        powerFactor: inverse.powerFactor,
        tdLossPct: inverse.tdLossPct,
        inputHash: maxRung.inputHash,
        resultHash: maxRung.resultHash,
      }
      : null,
    ladder: sweep.rungs,
    landFitNonMonotonic: sweep.landFitNonMonotonic,
    // The placement the winning rung actually used. When it differs from the
    // scenario's own layout the Studio can APPLY it — otherwise the user is told
    // a capacity is reachable without being told how to reach it.
    ...(maxRung.landFit?.optimisedPlacement
      ? {
        recommendedPlacement: {
          values: maxRung.landFit.optimisedPlacement,
          placementsEvaluated: maxRung.landFit.placementsEvaluated,
        },
      }
      : {}),
    warnings,
  };
}
